import type { ReactElement } from 'react';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  DRILL_DOWN_CHIP_BOX_CLASS,
  DRILL_DOWN_CHIP_CLASS,
  DRILL_DOWN_CHIP_ICON_CLASS,
} from '@/styles/drillDownChip';
import { formatNumber } from '@/utils/number';

const DRILL_DOWN_COUNT_CLASS = cn(DRILL_DOWN_CHIP_CLASS, 'tabular-nums');
const ZERO_COUNT_CLASS = cn(DRILL_DOWN_CHIP_BOX_CLASS, 'tabular-nums text-muted-foreground');
const COUNT_LABEL_CLASS = 'mr-1 font-normal';

interface DrillDownCountProps {
  count: number;
  hint: string;
  label?: string | undefined;
  ariaLabel?: string | undefined;
  shouldShowHint?: boolean | undefined;
  render: ReactElement;
}

interface DrillDownCountButtonProps extends Omit<DrillDownCountProps, 'render'> {
  onOpen: () => void;
}

export function DrillDownCount({
  count,
  hint,
  label,
  ariaLabel = hint,
  shouldShowHint = true,
  render,
}: DrillDownCountProps) {
  if (count === 0) {
    return (
      <span className={ZERO_COUNT_CLASS}>
        {label === undefined ? null : <span className={COUNT_LABEL_CLASS}>{label}</span>}0
      </span>
    );
  }

  return (
    <Tooltip disabled={!shouldShowHint}>
      <TooltipTrigger
        render={render}
        aria-label={ariaLabel}
        className={DRILL_DOWN_COUNT_CLASS}
        onClick={(event) => event.stopPropagation()}
      >
        {label === undefined ? null : <span className={COUNT_LABEL_CLASS}>{label}</span>}
        {formatNumber(count)}
        <ChevronRight className={DRILL_DOWN_CHIP_ICON_CLASS} aria-hidden />
      </TooltipTrigger>
      <TooltipContent>{hint}</TooltipContent>
    </Tooltip>
  );
}

export function DrillDownCountButton({ onOpen, ...countProps }: DrillDownCountButtonProps) {
  return <DrillDownCount {...countProps} render={<button type="button" onClick={onOpen} />} />;
}
